import { useCallback, useMemo } from "react";

import type { NotifyPreferences } from "@/bryntum-lib/models/NotifyPrefrences";
import { NOTIFICATION_VISIBILITY_CODE } from "@/other/constants/OtherKeys.jsx";
import { STORE_KEYS } from "@/other/constants/StoreKeys.jsx";
import type { ISwivuProject } from "@/types/SwivuProject";
import type { ISwivuStore } from "@/types/SwivuStore";
import { useSwivuBryntumContext } from "@/react/hooks/useSwivuBryntumContext";
import { useSwivuStore } from "@/react/hooks/useSwivuStore";
import { useNotificationScope } from "./useNotificationScope";

export type NotificationVisibilityMode =
  | typeof NOTIFICATION_VISIBILITY_CODE.Mine
  | typeof NOTIFICATION_VISIBILITY_CODE.All;

export interface NotifyPreferenceRecord {
  id: string | number;
  userId?: number;
  notifyType: number;
  isEmailNotify: boolean;
  isWorkforceNotify: boolean;
  interupt: boolean;
  bannerNotify: number;
  order: number;
}

const DEFAULT_MODE = NOTIFICATION_VISIBILITY_CODE.Mine;
const TIE_BREAK_ORDER: NotificationVisibilityMode[] = [
  NOTIFICATION_VISIBILITY_CODE.Mine,
  NOTIFICATION_VISIBILITY_CODE.All,
];

function toVisibilityMode(value: unknown): NotificationVisibilityMode | null {
  const n = Number(value);
  if (n === NOTIFICATION_VISIBILITY_CODE.All) return NOTIFICATION_VISIBILITY_CODE.All;
  if (n === NOTIFICATION_VISIBILITY_CODE.Mine || n === NOTIFICATION_VISIBILITY_CODE.None) return DEFAULT_MODE;
  return null;
}

export function getNotifyPreferenceStore(
  project: ISwivuProject | null | undefined,
): ISwivuStore<NotifyPreferences> | null {
  return project?.notifyPreferenceStore ?? null;
}

function getStorePreferenceRecords(
  store: ISwivuStore<NotifyPreferences>,
  userId?: number | null,
): NotifyPreferenceRecord[] {
  const fromRecords = store.records;
  const all: NotifyPreferenceRecord[] = fromRecords?.length
    ? (fromRecords as unknown as NotifyPreferenceRecord[])
    : ((store.query(() => true, true) ?? []) as unknown as NotifyPreferenceRecord[]);
  return userId == null ? all : all.filter((r) => r.userId === userId);
}

export function getNotifyPreferenceRecords(
  project: ISwivuProject | null | undefined,
  userId?: number | null,
): NotifyPreferenceRecord[] {
  const store = getNotifyPreferenceStore(project);
  return store ? getStorePreferenceRecords(store, userId) : [];
}

function getGlobalNotificationVisibility(
  records: ReadonlyArray<Pick<NotifyPreferenceRecord, "bannerNotify"> | null | undefined>,
): NotificationVisibilityMode {
  const modes = records
    .map((r) => toVisibilityMode(r?.bannerNotify))
    .filter((m): m is NotificationVisibilityMode => m != null);
  if (modes.length === 0) return DEFAULT_MODE;
  const first = modes[0];
  if (modes.every((m) => m === first)) return first;
  const counts = new Map<NotificationVisibilityMode, number>();
  for (const mode of modes) counts.set(mode, (counts.get(mode) ?? 0) + 1);
  let best: NotificationVisibilityMode = DEFAULT_MODE;
  let bestCount = -1;
  for (const candidate of TIE_BREAK_ORDER) {
    const count = counts.get(candidate) ?? 0;
    if (count > bestCount) { best = candidate; bestCount = count; }
  }
  return best;
}

function setGlobalNotificationVisibility(
  store: ISwivuStore<NotifyPreferences> | null | undefined,
  mode: NotificationVisibilityMode,
  userId?: number | null,
): void {
  if (!store) return;
  for (const record of getStorePreferenceRecords(store, userId)) {
    if (Number(record.bannerNotify) !== mode) record.bannerNotify = mode;
  }
}

export function useNotificationVisibility() {
  const { project, user } = useSwivuBryntumContext();
  const scope = useNotificationScope();
  const { version: notifyPreferenceRevision } = useSwivuStore({
    storeId: STORE_KEYS.notifyPreferenceStore,
  });
  const currentUserId = user?.id ?? undefined;

  const visibilityMode = useMemo((): NotificationVisibilityMode => {
    if (scope.isAccountView) return NOTIFICATION_VISIBILITY_CODE.Mine;
    return getGlobalNotificationVisibility(
      getNotifyPreferenceRecords(project, currentUserId),
    );
  }, [currentUserId, notifyPreferenceRevision, project, scope.isAccountView]);

  const setVisibilityMode = useCallback(
    (mode: NotificationVisibilityMode) => {
      setGlobalNotificationVisibility(
        getNotifyPreferenceStore(project),
        mode,
        currentUserId,
      );
    },
    [currentUserId, project],
  );

  return { visibilityMode, setVisibilityMode };
}
